import React from 'react';
import { Card, Button, Descriptions, Typography, Empty } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useFlowObserve, formatDateTime } from '../hooks/useFlowObserve';
import ObserveControlCard from './FlowObserve/ObserveControlCard';
import LiveAuditPanel from './FlowObserve/LiveAuditPanel';
import ComparisonSection from './FlowObserve/ComparisonSection';
import RadarSection from './FlowObserve/RadarSection';
import HandoffSection from './FlowObserve/HandoffSection';

const { Text } = Typography;

const FlowObservePanel: React.FC = () => {
  const {
    chainId,
    setChainId,
    docPrefix,
    setDocPrefix,
    scenarioId,
    initializing,
    starting,
    stopping,
    obsWindow,
    isActive,
    auditCount,
    audits,
    expected,
    receipt,
    receiptLoading,
    docNo,
    setDocNo,
    handoffResults,
    runningCheckId,
    runningAll,
    start,
    stop,
    runOne,
    runAll,
    copySummary,
  } = useFlowObserve();

  const showReceipt = !!receipt && !isActive;

  return (
    <div>
      {/* A. 控制區 */}
      <div className="mb-6">
        <ObserveControlCard
          obsWindow={obsWindow}
          isActive={isActive}
          auditCount={auditCount}
          chainId={chainId}
          docPrefix={docPrefix}
          scenarioId={scenarioId}
          initializing={initializing}
          starting={starting}
          stopping={stopping}
          onChainChange={setChainId}
          onDocPrefixChange={setDocPrefix}
          onStart={start}
          onStop={stop}
        />
      </div>

      {/* B. 即時寫入回執（觀察中） */}
      {isActive && (
        <div className="mb-6">
          <LiveAuditPanel
            audits={audits}
            auditCount={auditCount}
            whitelistTables={expected?.whitelistTables || []}
          />
        </div>
      )}

      {/* C. 回執報告（結束後） */}
      {receiptLoading && (
        <Card loading title="回執報告" className="mb-6" />
      )}

      {showReceipt && receipt && (
        <>
          {/* 觀察窗摘要 */}
          <div className="mb-6">
            <Card title="觀察窗摘要" size="small">
              <Descriptions size="small" column={{ xs: 1, md: 3 }}>
                <Descriptions.Item label="Window ID">#{receipt.window.id}</Descriptions.Item>
                <Descriptions.Item label="Chain">
                  <span className="font-mono">{receipt.window.chainId}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Scenario">
                  <span className="font-mono">{receipt.window.scenarioId}</span>
                </Descriptions.Item>
                <Descriptions.Item label="單號前綴">
                  <span className="font-mono">{receipt.window.docPrefix}</span>
                </Descriptions.Item>
                <Descriptions.Item label="開始時間">{formatDateTime(receipt.window.startedAt)}</Descriptions.Item>
                <Descriptions.Item label="結束時間">{formatDateTime(receipt.window.stoppedAt)}</Descriptions.Item>
              </Descriptions>
            </Card>
          </div>

          {/* 比對結論（最顯眼） */}
          <div className="mb-6">
            <ComparisonSection comparison={receipt.comparison} />
          </div>

          {/* 全域雷達（預設收合） */}
          <div className="mb-6">
            <RadarSection radar={receipt.radar} />
          </div>

          {/* 交棒驗證 */}
          <div className="mb-6">
            <HandoffSection
              checks={receipt.expected?.handoffChecks || []}
              results={handoffResults}
              docNo={docNo}
              runningCheckId={runningCheckId}
              runningAll={runningAll}
              onDocNoChange={setDocNo}
              onRunCheck={runOne}
              onRunAll={runAll}
            />
          </div>

          {/* 匯出 */}
          <div className="mb-6">
            <Card size="small">
              <div className="flex items-center gap-4">
                <Button icon={<CopyOutlined />} onClick={copySummary}>
                  複製驗證摘要 (Markdown)
                </Button>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  包含觀察窗資訊、Expected vs Actual 比對、交棒驗證結果，可直接貼到 validation card
                </Text>
              </div>
            </Card>
          </div>
        </>
      )}

      {/* 空狀態：無活動窗也無回執 */}
      {!initializing && !isActive && !receipt && !receiptLoading && (
        <Card>
          <Empty description="目前沒有進行中的觀察窗，設定 Chain 與單號前綴後按「開始觀察」" />
        </Card>
      )}
    </div>
  );
};

export default FlowObservePanel;
